import { useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { dateFormattedUTC, numberWithCommas } from "modules/functions";
import Layout from "components/Layout";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import PageTitle from "components/text/PageTitle";
import Tip from "components/other/Tip";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ModalWrap from "components/other/ModalWrap";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";

import exchangeIcon from "assets/img/icons/icons8-exchange.svg";
import coinsIcon from "assets/img/icons/coins-icon.svg";
import arrowIcon from "assets/img/icons/icons8-arrow.svg";
import dollarIcon from "assets/img/icons/dollar-icon.svg";
import warningIcon from "assets/img/icons/warning-triangle-icon.svg";
import newTabIcon from "assets/img/icons/Expand-Window.svg";
import returnIcon from "assets/img/icons/return-icon.svg";

const ConvertCoins = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  margin: 0 0 50px;
  padding: 0 28px;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin: 0;
    padding: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const ConvertCoinsBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_XLARGE};
`;

const BoxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  flex: 1;
`;

const BoxTitle = styled.div`
  width: 100%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};

  display: flex;
  align-items: center;
  gap: 8px;
`;

const BoxContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  flex: 1;
`;

const ConversionIcon = styled.div`
  width: 30px;
  min-width: 30px;
  height: 30px;
`;

const Text = styled.div`
  line-height: 125%;
`;

const FlatBox = styled.div`
  width: 100%;
  max-width: ${(props) => props.maxwidth};
  padding: ${css.BOX_PADDING_SECONDARY};
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  flex: 1;
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const DataIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const DataText = styled.div`
  white-space: nowrap;
`;

const DataRowIconWrap = styled.div`
  padding: 0 4px;
`;

const ExchangeRateSmallInfo = styled.div`
  font-size: 13px;
  color: ${css.TEXT_GREY};
  line-height: 125%;
`;

const TipBox = styled.div`
  /* max-width: 315px; */
  padding: ${css.BOX_PADDING_SECONDARY};
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TipWrap = styled.div`
  display: flex;
  align-items: center;
`;

const TipIcon = styled.div`
  width: 22px;
  min-width: 22px;
  height: 22px;
`;

const TipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const TipText = styled.div`
  padding-left: 12px;

  flex: 1;
`;

const ExternalLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 6px;

  text-decoration: none;
`;

const ExternalLinkText = styled.div`
  color: ${css.LIGHT_BLUE};
`;

const ExternalLinkIcon = styled.div`
  padding-top: 2px;

  width: 11px;
  min-width: 11px;
`;

const ConvertButtonWrap = styled.div`
  margin-top: 10px;

  display: flex;
`;

const Warning = styled.div`
  margin-top: ${(props) => (props.margintop ? props.margintop : 0)};

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const PayoutNotSet = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const PayoutNotSetWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PayoutNotSetText = styled.div`
  line-height: 125%;
`;

const PayoutNotSetButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

export default ({
  data,
  amount,
  onAmountChange,
  warnings,
  onConvert,
  conversionError,
  conversionLoading,
  payoutNotSet,
}) => {
  const navigate = useNavigate();

  // set page Title
  useEffect(() => {
    document.title = "Convert Coins | Scalelup";
  }, []);

  const balance = data?.coinCoversionData.balanceToConvert;
  const exchangeRate = data?.coinCoversionData.exchangeRate;

  const receiveCash = Math.round((amount / 1000) * exchangeRate * 100) / 100;

  return (
    <Layout>
      <ConvertCoins>
        <Header>
          <PageTitle>Convert Coins</PageTitle>
          <BackIconWrap>
            <RoundActionIcon
              icon={returnIcon}
              iconSize="16px"
              iconAlt="Return"
              onClick={() => navigate("/wallet")}
            />
          </BackIconWrap>
        </Header>
        {data && (
          <ConvertCoinsBox>
            <BoxSection>
              <BoxTitle>
                <ConversionIcon>
                  <img src={exchangeIcon} style={{ width: "100%" }} />
                </ConversionIcon>
                Convert Coins to Cash
              </BoxTitle>
              <BoxContent>
                <Text>
                  Scalelup coins are primarily intended for displaying your
                  posts to other users, allowing you to gain attention for your
                  product, service, or business opportunity. If you don't plan
                  to use the option to promote your offer on the Scalelup
                  platform, you can convert the coins you've earned into money.
                  The money obtained from the conversion will be transferred to
                  your cash balance.
                </Text>
                <FlatBox maxwidth="350px">
                  <Data>
                    <DataTitle>Current Exchange Rate</DataTitle>
                    <DataRow>
                      <DataIcon>
                        <img src={coinsIcon} style={{ width: "100%" }} />
                      </DataIcon>
                      <DataText>{numberWithCommas(1000)}</DataText>
                      <DataRowIconWrap>
                        <DataIcon>
                          <img src={arrowIcon} style={{ width: "100%" }} />
                        </DataIcon>
                      </DataRowIconWrap>
                      <DataIcon>
                        <img src={dollarIcon} style={{ width: "100%" }} />
                      </DataIcon>
                      <DataText>{exchangeRate} USD</DataText>
                    </DataRow>
                  </Data>
                  <ExchangeRateSmallInfo>
                    * The exchange rate is subject to change at any time.
                  </ExchangeRateSmallInfo>
                </FlatBox>
                <TipBox>
                  <TipWrap>
                    <TipIcon>
                      <img
                        src={warningIcon}
                        alt="Tip"
                        style={{ width: "100%" }}
                      />
                    </TipIcon>
                    <TipContent>
                      <TipText>
                        Please be aware of the minimum withdrawal amounts for
                        each payment method. Also, note that withdrawals are
                        processed at the beginning of each calendar month.
                      </TipText>
                      <TipText>
                        <ExternalLink
                          to="https://help.scalelup.com/en/articles/10542199-what-is-the-minimum-amount-for-withdrawal"
                          target="_blank"
                        >
                          <ExternalLinkText>
                            Minimum withdrawal limits
                          </ExternalLinkText>
                          <ExternalLinkIcon>
                            <img src={newTabIcon} style={{ width: "100%" }} />
                          </ExternalLinkIcon>
                        </ExternalLink>
                      </TipText>
                    </TipContent>
                  </TipWrap>
                </TipBox>
              </BoxContent>
            </BoxSection>
            <BoxSection>
              <BoxTitle>Conversion</BoxTitle>
              <BoxContent>
                <Text>
                  Enter the number of coins you want to exchange for money
                  (5,000 coins minimum). Carefully review the conversion.
                </Text>
                <Data>
                  <DataTitle>
                    Available for Conversion
                    <Tip
                      tipId="coinsAvailable"
                      tip="Purchased coins cannot be converted. Such coins are not included in the balance available for conversion."
                    />
                  </DataTitle>
                  <DataRow>
                    <DataIcon>
                      <img src={coinsIcon} style={{ width: "100%" }} />
                    </DataIcon>
                    <DataText>{numberWithCommas(balance)}</DataText>
                  </DataRow>
                </Data>
                <FlatBox maxwidth="280px">
                  <InputWithLabel label="Amount to Convert" width="100%">
                    <InputText
                      type="text"
                      name="link"
                      value={amount}
                      placeholder="Number of coins"
                      onChange={onAmountChange}
                      maxLength={8}
                      warning={warnings.includes("amount")}
                    />{" "}
                    {conversionError === "amountOverBalance" && (
                      <Warning margintop="7px">
                        The entered value is higher than the balance available
                        for conversion.
                      </Warning>
                    )}
                    {conversionError === "amountTooSmall" && (
                      <Warning margintop="7px">
                        The minimum amount of coins for conversion is 5,000.
                      </Warning>
                    )}
                  </InputWithLabel>
                  <Data>
                    <DataTitle>Conversion Overview</DataTitle>
                    <DataRow>
                      <DataIcon>
                        <img src={coinsIcon} style={{ width: "100%" }} />
                      </DataIcon>
                      <DataText>
                        {amount !== "" ? numberWithCommas(amount) : 0}
                      </DataText>
                      <DataRowIconWrap>
                        <DataIcon>
                          <img src={arrowIcon} style={{ width: "100%" }} />
                        </DataIcon>
                      </DataRowIconWrap>
                      <DataIcon>
                        <img src={dollarIcon} style={{ width: "100%" }} />
                      </DataIcon>
                      <DataText>{receiveCash} USD</DataText>
                    </DataRow>
                  </Data>
                  <ConvertButtonWrap>
                    <ButtonPrimary
                      onClick={onConvert}
                      disabled={conversionLoading}
                    >
                      Convert
                    </ButtonPrimary>
                  </ConvertButtonWrap>
                  {conversionError === "lowBalance" && (
                    <Warning margintop="-7px">
                      The available balance is too low.
                    </Warning>
                  )}
                  {conversionError === "conversionError" && (
                    <Warning margintop="-7px">
                      An error occurred during the conversion, please try again
                      or contact support.
                    </Warning>
                  )}
                </FlatBox>
                {/* <FlatBox>
                  <Text>
                    The exchange of coins for cash is currently unavailable. It
                    is scheduled to resume on October 8th.
                  </Text>
                </FlatBox> */}
              </BoxContent>
            </BoxSection>
          </ConvertCoinsBox>
        )}
        {!data && (
          <LoadingBox>
            <BoxLoadingSpinner height="218px" />
          </LoadingBox>
        )}
        {payoutNotSet && (
          <ModalWrap>
            <Backdrop onClick={() => {}} background="rgba(0, 0, 0, 0.7)" />
            <PayoutNotSet>
              <ModalWindow
                title="Enter Payout Details"
                handleModalClose={() => navigate("/settings")}
                fullScreenHeight={true}
              >
                <PayoutNotSetWrap>
                  <PayoutNotSetText>
                    Before you start exchanging your coins for cash, please set
                    up your payout details. We need this information in order to
                    pay out your earnings.
                  </PayoutNotSetText>
                  <PayoutNotSetButtons>
                    <ButtonPrimary onClick={() => navigate("/settings")}>
                      Setup Payout
                    </ButtonPrimary>
                  </PayoutNotSetButtons>
                </PayoutNotSetWrap>
              </ModalWindow>
            </PayoutNotSet>
          </ModalWrap>
        )}
      </ConvertCoins>
    </Layout>
  );
};
